import * as React from 'react';
import { createRoute, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { PayrollItemSummary } from '@bits/shared/zod';

export const payrollDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payroll/$id',
  component: PayrollDetailPage,
});

type PayrollItemSortKey = 'person' | 'paidDays' | 'gross' | 'deductions' | 'net' | 'employerCost';

const EMPTY_ITEMS: PayrollItemSummary[] = [];

function deductionTotal(item: PayrollItemSummary): number {
  return item.deductions.reduce((s, x) => s + x.amountPaise, 0);
}

const PAYROLL_ITEM_SORT_ACCESSORS: SortAccessors<PayrollItemSummary, PayrollItemSortKey> = {
  person: (i) => i.personName ?? i.personId,
  paidDays: (i) => i.paidDaysX10,
  gross: (i) => i.grossPaise,
  deductions: deductionTotal,
  net: (i) => i.netPaise,
  employerCost: (i) => i.employerCostPaise,
};

function formatRupees(paise: number): string {
  return (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function formatDaysX10(x10: number): string {
  const whole = Math.trunc(x10 / 10);
  const frac = Math.abs(x10 % 10);
  return frac === 0 ? String(whole) : `${whole}.${frac}`;
}

function PayrollDetailPage(): JSX.Element {
  const { id } = useParams({ from: payrollDetailRoute.id });
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);
  const downloadPayslip = async (itemId: string, personLabel: string) => {
    try {
      const blob = await api.downloadPayslip(id, itemId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip-${personLabel.replace(/[^A-Za-z0-9_-]+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setError(null);
    } catch (err) {
      setError(toMessage(err));
    }
  };


  const detailQuery = useQuery({
    queryKey: ['payroll-run', id],
    queryFn: () => api.getPayrollRun(id),
  });

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['payroll-run', id] }),
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] }),
    ]);
  };

  const preview = useMutation({
    mutationFn: () => api.previewPayrollRun(id),
    onSuccess: async () => { setError(null); await invalidate(); },
    onError: (err) => setError(toMessage(err)),
  });

  const lock = useMutation({
    mutationFn: () => api.lockPayrollRun(id),
    onSuccess: async () => { setError(null); await invalidate(); },
    onError: (err) => setError(toMessage(err)),
  });

  const unlock = useMutation({
    mutationFn: () => api.unlockPayrollRun(id),
    onSuccess: async () => { setError(null); await invalidate(); },
    onError: (err) => setError(toMessage(err)),
  });

  const post = useMutation({
    mutationFn: () => api.postPayrollRun(id),
    onSuccess: async () => { setError(null); await invalidate(); },
    onError: (err) => setError(toMessage(err)),
  });

  const d = detailQuery.data;
  const { rows: items, ...sorter } = useSortedRows(d?.items ?? EMPTY_ITEMS, PAYROLL_ITEM_SORT_ACCESSORS);
  const canPreview = d?.status === 'draft' || d?.status === 'preview';
  const canLock = d?.status === 'preview';
  const canUnlock = d?.status === 'locked';
  const canPost = d?.status === 'locked';

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            {d ? `${d.month} · ${d.kind === 'regular' ? 'Regular' : 'Off-cycle'}` : 'Loading…'}
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            {d
              ? `${d.itemCount} item(s) · net ₹${formatRupees(d.totalNetPaise)} · employer cost ₹${formatRupees(d.totalEmployerCostPaise)}`
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => preview.mutate()}
            disabled={!canPreview || preview.isPending}
          >
            {preview.isPending ? 'Previewing…' : d?.status === 'preview' ? 'Re-preview' : 'Preview'}
          </Button>
          <Button onClick={() => lock.mutate()} disabled={!canLock || lock.isPending}>
            {lock.isPending ? 'Locking…' : 'Lock'}
          </Button>
          <Button
            variant="outline"
            onClick={() => unlock.mutate()}
            disabled={!canUnlock || unlock.isPending}
          >
            {unlock.isPending ? 'Unlocking…' : 'Unlock'}
          </Button>
          <Button variant="destructive" onClick={() => post.mutate()} disabled={!canPost || post.isPending}>
            {post.isPending ? 'Posting…' : 'Post'}
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Status</CardTitle>
          <CardDescription>
            {d ? (
              <>
                <Badge>{d.status}</Badge>
                {d.postedAt ? ` · posted ${new Date(d.postedAt).toISOString().slice(0, 19).replace('T', ' ')} UTC` : ''}
              </>
            ) : (
              'Loading…'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {d?.notes ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{d.notes}</p>
          ) : (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No notes.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Items</CardTitle>
          <CardDescription>
            {d?.items.length ?? 0} people — populated at preview
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!d || d.items.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              No items yet. Run Preview to compute paid days → gross → deductions → net.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  <tr className="border-b border-[hsl(var(--border))]">
                    <SortableTh sortKey="person" controller={sorter} className="py-2 pr-4 font-medium">
                      Person
                    </SortableTh>
                    <SortableTh sortKey="paidDays" controller={sorter} className="py-2 pr-4 font-medium">
                      Paid days
                    </SortableTh>
                    <SortableTh sortKey="gross" controller={sorter} align="right" className="py-2 pr-4 font-medium">
                      Gross
                    </SortableTh>
                    <SortableTh sortKey="deductions" controller={sorter} align="right" className="py-2 pr-4 font-medium">
                      Deductions
                    </SortableTh>
                    <SortableTh sortKey="net" controller={sorter} align="right" className="py-2 pr-4 font-medium">
                      Net
                    </SortableTh>
                    <SortableTh sortKey="employerCost" controller={sorter} align="right" className="py-2 pr-4 font-medium">
                      Employer cost
                    </SortableTh>
                    <th className="py-2 pr-4 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => {
                    const dedTotal = deductionTotal(i);
                    return (
                      <tr key={i.id} className="border-b border-[hsl(var(--border))]/60">
                        <td className="py-2 pr-4">
                          <div className="font-medium">{i.personName ?? i.personId}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            {i.deductions.map((x) => `${x.name}: ₹${formatRupees(x.amountPaise)}`).join(' · ') || 'no deductions'}
                          </div>
                        </td>
                        <td className="py-2 pr-4">{formatDaysX10(i.paidDaysX10)}</td>
                        <td className="py-2 pr-4 text-right">₹ {formatRupees(i.grossPaise)}</td>
                        <td className="py-2 pr-4 text-right">₹ {formatRupees(dedTotal)}</td>
                        <td className="py-2 pr-4 text-right font-semibold">₹ {formatRupees(i.netPaise)}</td>
                        <td className="py-2 pr-4 text-right">₹ {formatRupees(i.employerCostPaise)}</td>
                        <td className="py-2 pr-4 text-right">
                          {d.status === 'locked' || d.status === 'posted' ? (
                            <Button variant="ghost" size="sm" onClick={() => downloadPayslip(i.id, i.personName ?? i.personId)}>
                              Payslip
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {d && d.allocations.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Ledger allocations</CardTitle>
            <CardDescription>
              {d.allocations.length} row(s) written to ledger at post time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {d.allocations.map((a) => (
                <li key={`${a.personId}|${a.projectId}`} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div>
                    <div className="font-medium">{a.personId}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {a.projectCode ? `${a.projectCode} — ` : ''}{a.projectName ?? a.projectId}
                    </div>
                  </div>
                  <div>₹ {formatRupees(a.amountPaise)}</div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </AppShell>
  );
}
